import { useOnce } from '@opallabs/lambda'
import { TabName, orderedTabs } from './useSelectedPrimaryTab'

export const fragment = '#personal_panel'
export const openParam = 'open_personal_panel'

type InitialTab = TabName | undefined

type UseUrlFragment = (arg: { callback?: () => void }) => {
  initialTab: InitialTab
}

const isValidTab = (tabName: unknown): tabName is TabName =>
  typeof tabName === 'string' && orderedTabs.includes(tabName as TabName)

/**
 * Check if PersonalPanel should auto-open based on URL.
 * When embedded in an iframe, check the iframe's own location (not parent).
 * Supports both hash fragment (#personal_panel) and query param (open_personal_panel=true).
 */
const useUrlFragment: UseUrlFragment = ({ callback }) => {
  // When embedded in an iframe, use the iframe's location, not the parent's
  // This allows Asgard to load the iframe with ?open_personal_panel=true
  const isEmbedded = (window as Window & { embedded?: boolean }).embedded
  const location = isEmbedded
    ? window.location
    : (window.top ?? window).location

  const fragmentIncludesPersonalPanel = location.hash === fragment
  const hasOpenParam =
    new URLSearchParams(location.search).get(openParam) === 'true'
  const shouldOpen = fragmentIncludesPersonalPanel || hasOpenParam

  useOnce(() => {
    if (shouldOpen) {
      callback?.()
    }
  })

  const tabParam = new URLSearchParams(location.search).get('tab')
  const initialTab: InitialTab =
    shouldOpen && isValidTab(tabParam) ? tabParam : undefined

  return { initialTab }
}

export { useUrlFragment }
